import { useState, useEffect } from 'react';
import { BulkBlockDeal } from '../types';
import {
  fetchBulkBlockDeals,
  computeStockDealBadge,
  StockDealBadgeInfo,
} from '../services/bulkBlockDealsService';

export type { StockDealBadgeInfo };

// Module-level caches for fast, shared resolution across all chart instances & tables
let allDealsCache: BulkBlockDeal[] | null = null;
const dealsBySymbolCache = new Map<string, BulkBlockDeal[]>();
const badgesBySymbolCache = new Map<string, StockDealBadgeInfo | null>();
const badgeListeners = new Set<() => void>();
let pendingFetch: Promise<BulkBlockDeal[]> | null = null;

const notifyBadgeListeners = () => {
  badgeListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener error
    }
  });
};

/**
 * Normalizes symbols by trimming whitespace, uppercase, and stripping exchange suffixes (.NS, .BO)
 */
export const normalizeDealSymbol = (sym?: string | null): string =>
  (sym || '').trim().toUpperCase().replace(/\.NS$|\.BO$/i, '');

/**
 * Ensures bulk & block deals tape is loaded and indexed into memory.
 */
export const ensureBulkDealsLoaded = async (): Promise<BulkBlockDeal[]> => {
  if (allDealsCache) {
    return allDealsCache;
  }

  if (!pendingFetch) {
    pendingFetch = fetchBulkBlockDeals()
      .then((deals) => {
        allDealsCache = deals;
        // Index all deals by normalized symbol for O(1) subsequent lookups
        for (const d of deals) {
          const sym = normalizeDealSymbol(d.symbol);
          const list = dealsBySymbolCache.get(sym) || [];
          list.push(d);
          dealsBySymbolCache.set(sym, list);
        }

        // Precompute badges for all symbols with deals
        dealsBySymbolCache.forEach((list, sym) => {
          badgesBySymbolCache.set(sym, computeStockDealBadge(list));
        });

        notifyBadgeListeners();
        return deals;
      })
      .finally(() => {
        pendingFetch = null;
      });
  }

  return pendingFetch;
};

/**
 * Resolves bulk and block deals for a given stock symbol from the shared tape cache.
 */
export const getStockBulkBlockDeals = async (symbol?: string | null): Promise<BulkBlockDeal[]> => {
  const clean = normalizeDealSymbol(symbol);
  if (!clean) return [];

  if (dealsBySymbolCache.has(clean)) {
    return dealsBySymbolCache.get(clean)!;
  }

  await ensureBulkDealsLoaded();
  return dealsBySymbolCache.get(clean) || [];
};

/**
 * Synchronous badge lookup if deals tape is already in memory.
 */
export const getStockDealBadge = (symbol?: string | null): StockDealBadgeInfo | null => {
  const clean = normalizeDealSymbol(symbol);
  if (!clean) return null;
  if (badgesBySymbolCache.has(clean)) {
    return badgesBySymbolCache.get(clean) ?? null;
  }
  const deals = dealsBySymbolCache.get(clean);
  if (deals && deals.length > 0) {
    const badge = computeStockDealBadge(deals);
    badgesBySymbolCache.set(clean, badge);
    return badge;
  }
  return null;
};

/**
 * High-performance hook for rendering an institutional signal badge next to any stock symbol.
 * Shares a single global listener and updates all rows simultaneously when tape finishes loading.
 */
export const useStockDealBadge = (symbol?: string | null): StockDealBadgeInfo | null => {
  const clean = normalizeDealSymbol(symbol);
  const [badge, setBadge] = useState<StockDealBadgeInfo | null>(() => getStockDealBadge(clean));

  useEffect(() => {
    setBadge(getStockDealBadge(clean));

    // If deals are already loaded in memory, no need to register a listener
    if (allDealsCache) {
      return;
    }

    // If not yet loaded into memory, trigger background fetch
    void ensureBulkDealsLoaded();

    const listener = () => setBadge(getStockDealBadge(clean));
    badgeListeners.add(listener);
    return () => {
      badgeListeners.delete(listener);
    };
  }, [clean]);

  return badge;
};

export interface UseStockBulkBlockDealsResult {
  deals: BulkBlockDeal[];
  loading: boolean;
  error: string | null;
}

/**
 * Hook to retrieve institutional bulk and block deals for a specific stock ticker.
 * Shares an in-memory cache across modals, historical charts, and screeners.
 */
export const useStockBulkBlockDeals = (symbol?: string | null): UseStockBulkBlockDealsResult => {
  const clean = normalizeDealSymbol(symbol);

  const [deals, setDeals] = useState<BulkBlockDeal[]>(() => {
    if (!clean) return [];
    return dealsBySymbolCache.get(clean) ?? [];
  });

  const [loading, setLoading] = useState<boolean>(() => {
    if (!clean) return false;
    return !dealsBySymbolCache.has(clean);
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clean) {
      setDeals([]);
      setLoading(false);
      return;
    }

    if (dealsBySymbolCache.has(clean)) {
      setDeals(dealsBySymbolCache.get(clean)!);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    getStockBulkBlockDeals(clean)
      .then((items) => {
        if (isMounted) {
          setDeals(items);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load bulk & block deals');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [clean]);

  return { deals, loading, error };
};
